import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { useSpinDelay } from 'spin-delay'

import { cn } from '#app/utils/misc.tsx'
import { pressScale } from './motion.ts'
import { Spinner } from './spinner.tsx'

const buttonVariants = cva(
	`focus-cosy ${pressScale} text-body-xs inline-flex items-center justify-center rounded-md font-medium outline-hidden disabled:pointer-events-none disabled:opacity-50`,
	{
		variants: {
			variant: {
				// Solid semantic fills brighten on hover (customizer-safe — no opacity
				// mix that would wash the accent). The neutral fills ride the ramp.
				default: 'bg-primary text-primary-foreground hover:brightness-[1.06]',
				destructive:
					'bg-destructive text-destructive-foreground hover:brightness-[1.06]',
				outline: 'border-input bg-background hover:bg-[var(--bg-hover)] border',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-[var(--bg-hover)]',
				ghost: 'hover:bg-[var(--bg-hover)]',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-8 px-3',
				wide: 'h-8 w-full',
				sm: 'h-7 px-2.5',
				lg: 'h-10 px-5',
				pill: 'h-8 rounded-full px-5',
				icon: 'size-8',
				'icon-sm': 'size-7',
				'icon-lg': 'size-10',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export type ButtonVariant = VariantProps<typeof buttonVariants>

const Button = ({
	className,
	variant,
	size,
	asChild = false,
	pending = false,
	spinDelay,
	disabled,
	children,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		asChild?: boolean
		/**
		 * Show a spin-delay-gated Spinner in place of the label and block
		 * interaction. The label stays mounted (just hidden) so the button keeps
		 * its width — no layout jump. For rich success/error-in-button feedback
		 * reach for StatusButton instead.
		 */
		pending?: boolean
		/** Tuning for the pending-spinner delay (see the `spin-delay` package). */
		spinDelay?: Parameters<typeof useSpinDelay>[1]
	}) => {
	const showSpinner = useSpinDelay(pending, {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})
	// asChild composes onto a single arbitrary child (Radix Slot), so the pending
	// overlay only applies to the native button; Slot children are passed through.
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot="button"
			className={cn(
				buttonVariants({ variant, size, className }),
				!asChild && showSpinner && 'relative',
			)}
			disabled={asChild ? disabled : disabled || pending}
			aria-busy={!asChild && pending ? true : undefined}
			{...props}
		>
			{!asChild && showSpinner ? (
				<>
					{/* Decorative — the button conveys busy via aria-busy; the label
					    stays in the a11y tree (opacity-0) so its name is unchanged. */}
					<span
						aria-hidden
						className="absolute inset-0 flex items-center justify-center"
					>
						<Spinner size="sm" />
					</span>
					<span className="opacity-0">{children}</span>
				</>
			) : (
				children
			)}
		</Comp>
	)
}

export { Button, buttonVariants }
